import { call, put, select } from 'redux-saga/effects';
import * as actions from './actions';
import { produce } from 'immer';
import axios from 'axios';
import moment from 'moment';
import { sagas as eventSagas } from 'store/event';
import { updateUnsubscribeFilters } from './actions';
import { getParams } from 'store/helpers/helperFunctions';

const { waitForGetSupportedEventTypes } = eventSagas;

const getTransformedSubscription = (subscription, campaignsList) => {
  subscription.allPromotionalCampaignsString = subscription.allPromotionalCampaigns ? 'Unsubscribed' : 'Subscribed';
  subscription.allTransactionalCampaignsString = subscription.allTransactionalCampaigns ? 'Unsubscribed' : 'Subscribed';
  subscription.campaignUuids = subscription.campaignUuids.map(campaign => {
    let currentCampaign = campaignsList.find(c => c.uuid === campaign);
    if (currentCampaign) {
      return {
        'label': currentCampaign.name,
        'value': currentCampaign.uuid,
        'link': `campaigns/edit/${currentCampaign.uuid}`
      };
    }
    return null;
  })
  .filter(Boolean)
  .sort((a, b) => a.label.localeCompare(b.label));
  subscription.excludedCampaigns = subscription.campaignUuids.map(campaign => campaign.label).join(', ');
};

// the actual http calls
const apiRoot = `${window._env_?.REACT_APP_API_PATH}`;

const getSubscriptionsApiRoot = (tenantId, businessContextId) =>
  `${apiRoot}tenants/${tenantId}/context/${businessContextId}/unsubscribe`;

export const getPaginatedSubscriptionsAsync = (
  tenantId,
  businessContextId,
  urlParams,
  bodyParams
) => {
  return axios
    .post(
      `${getSubscriptionsApiRoot(tenantId, businessContextId)}/search?index=${
        urlParams.index
      }&pageSize=${urlParams.pageSize}`,
      bodyParams
    )
    .then(response => ({
      data: response.data,
      totalRecords: parseInt(response.headers['x-ag-totalcount'])
    }))
    .catch(err => {
      throw err;
    });
};

export const updateResubscribeAsync = (
  tenantId, 
  businessContextId, 
  bodyParams
) => {
  return axios
    .post(
      `${getSubscriptionsApiRoot(tenantId, businessContextId)}/re-subscribe`,
      bodyParams
    )
    .then(response => response.data)
    .catch(err => {
      throw err;
    });
};

// selectors
const currentTenantId = store => store.user.selectedTenant.tenantId;
const currentBusinessContextId = store =>
  store.user.selectedBusinessContext.businessContextId;
const paginatedUnsubscribeList = store =>
  store.unSubscribe ? store.unSubscribe.paginatedList : [];
const pageNo = store =>
  store.unSubscribe.filters ? store.unSubscribe.filters?.pageNo : 0;
const searchInput = store =>
  store.unSubscribe.filters ? store.unSubscribe.filters?.searchInput : '';
const sortBy = store =>
  store.unSubscribe.filters ? store.unSubscribe.filters?.sortBy : 'emailAddress';
const sortOrder = store =>
  store.unSubscribe.filters ? store.unSubscribe.filters?.sortOrder : 'ASC';
const rowsPerPage = store =>
  store.unSubscribe.filters ? store.unSubscribe.filters?.rowsPerPage : 10;
const allCampaignsList = store => 
  store.campaign ? store.campaign.allCampaignsList : [];
const filterAllPromotional = store =>
  store.unSubscribe.filters ? store.unSubscribe.filters?.filterAllPromotional : [];
const filterAllTransactional = store =>
  store.unSubscribe.filters ? store.unSubscribe.filters?.filterAllTransactional : [];
const campaigns = store =>
  store.unSubscribe.filters ? store.unSubscribe.filters?.filterPaginatedCampaigns : [];

const getLocalParams = requestParams => {
  for (let param in requestParams) {
    if (param == 'emailAddresses') {
      if (requestParams[param].length == 0) {
        delete requestParams[param];
      } else {
        requestParams[param] = new Array(requestParams[param]);
      }
    }
    if (param === 'sortBy' && requestParams[param] === 'name') {
      requestParams[param] = 'emailAddress';
      requestParams['sortOrder'] = 'ASC';
    }
    if (param === 'campaignUuids') {
      requestParams[param] = requestParams[param].map(item => {
        return item.value;
      });
    }
    if (param === 'allPromotionalCampaigns' && (requestParams[param]?.length === 0 || requestParams[param]?.length === 2)) {
      delete requestParams[param];
    } else if (param === 'allPromotionalCampaigns' && requestParams[param]?.length === 1) {
      requestParams[param] = requestParams[param][0] === 'Unsubscribed' ? true : false;
    }
    if (param === 'allTransactionalCampaigns' && (requestParams[param]?.length === 0 || requestParams[param]?.length === 2)) {
      delete requestParams[param];
    } else if (param === 'allTransactionalCampaigns' && requestParams[param]?.length === 1) {
      requestParams[param] = requestParams[param][0] === 'Unsubscribed' ? true : false;
    }
  }
  return requestParams;
};
export function* getPaginatedSubscriptionsIfNeeded(action) {
  const { payload = {} } = action;
  const isPopupView = payload.isPopup;
  try {
    let tenantId = yield select(currentTenantId);
    let businessContextId = yield select(currentBusinessContextId);
    let currentList = yield select(paginatedUnsubscribeList);
    let currentCampaignList = yield select(allCampaignsList);
    yield call(waitForGetSupportedEventTypes);

    if (
      action.type === actions.GET_PAGINATED_SUBSCRIPTIONS &&
      (currentList.length === 0 || payload.force === true)
    ) {
      const requestBody = {
        emailAddresses: payload.emailAddress ? payload.emailAddress : yield select(searchInput),
        allPromotionalCampaigns: yield select(filterAllPromotional),
        allTransactionalCampaigns: yield select(filterAllTransactional),
        campaignUuids: yield select(campaigns),
        sortBy: yield select(sortBy),
        sortOrder: yield select(sortOrder)
      };
      let urlParams = {
        index: isPopupView ? 0 : yield select(pageNo),
        pageSize: isPopupView ? 1 : yield select(rowsPerPage)
      };
      let bodyParams = { ...getParams(getLocalParams(requestBody)) };
      currentList = yield call(
        getPaginatedSubscriptionsAsync,
        tenantId,
        businessContextId,
        urlParams,
        bodyParams
      );
      currentList.data = produce(currentList.data, subscription => {
        subscription = subscription.map(item => getTransformedSubscription(item, currentCampaignList));
      })
      if (!isPopupView) {
        yield put(updateUnsubscribeFilters({ totalCount: currentList.totalRecords }));
      }
    }
    yield put(actions.gettingPaginatedSubscriptionsSucceeded(currentList.data));
  } catch (ex) {
    if (!isPopupView) {
      yield put(updateUnsubscribeFilters({ totalCount: 0 }));
    }
    yield put(actions.gettingPaginatedSubscriptionsFailed(ex));
  }
}

export function* updateResubscribeIfNeeded(action) {
  const { payload = {} } = action;
  try {
    let tenantId = yield select(currentTenantId);
    let businessContextId = yield select(currentBusinessContextId);
    let reponse = yield call(
      updateResubscribeAsync,
      tenantId,
      businessContextId,
      payload
    );
    yield put(actions.updateResubscribeSucceeded(reponse));
  } catch (ex) {
    yield put(actions.updateResubscribeFailed(ex));
  }
}

export function* getUnsubscribeCount(action) {
  try {
    const tenantId = yield select(currentTenantId);
    const businessContextId = yield select(currentBusinessContextId);
    const { fromDate, toDate, campaignUuids } = action.payload;

    const bodyParams = {
      fromDate: moment(fromDate).startOf('day').toISOString(),
      toDate: moment(toDate).endOf('day').toISOString()
    };

    if (Array.isArray(campaignUuids) && campaignUuids.length > 0) {
      bodyParams.campaignUuids = campaignUuids;
    } else if (campaignUuids) {
      bodyParams.campaignUuids = [campaignUuids];
    }

    const response = yield call(
      axios.post,
      `${getSubscriptionsApiRoot(tenantId, businessContextId)}/search?index=0&pageSize=1`,
      bodyParams
    );
    const totalRecords = parseInt(response.headers['x-ag-totalcount'], 10) || 0;

    yield put(actions.getUnsubscribeCountSucceeded(totalRecords));
  } catch (e) {
    yield put(actions.getUnsubscribeCountFailed(e));
  }
}